/**
 * Your teams on this server: the list on the side (making one, or joining with a pasted link),
 * and the chosen team. `/` opens the team you looked at last, else the first one.
 */
import { Show, createEffect, type JSX } from "solid-js";
import { load } from "../shared/storage";
import { usePages } from "./context";
import { LAST_TEAM_KEY } from "./routes";
import { TeamList } from "./TeamList";
import { TeamPage } from "./TeamPage";
import { NewTeam } from "./NewTeam";

/** The dashboard (signed in). */
export function Dashboard(): JSX.Element {
  const { accounts, server, route, go } = usePages();
  const teams = () => accounts.me(server)?.teams ?? [];
  /** The team shown: the address's, when you are in it. */
  const shown = () => {
    const r = route();
    return r.page === "team" && teams().some((t) => t.id === r.team) ? r.team : null;
  };

  // At `/` (or a team you are no longer in), open the last team looked at, else the first.
  createEffect(() => {
    if (shown() || teams().length === 0) return;
    const last = load<string | null>(LAST_TEAM_KEY, null);
    const team = teams().find((t) => t.id === last) ?? teams()[0]!;
    go({ page: "team", team: team.id }, true);
  });

  return (
    <div class="dash" id="dashboard">
      <TeamList current={shown()} />
      <section class="dash-main">
        <Show
          when={shown()}
          fallback={
            <Show when={teams().length === 0}>
              <div class="pages-card" id="no-team">
                <h2>Make your first team</h2>
                <p class="muted">A team holds spaces: boards the team works on together, each with its own graph. You can invite people to it afterwards, each with a role.</p>
                <NewTeam id="first-team" />
              </div>
            </Show>
          }
        >
          {(id) => <TeamPage id={id()} />}
        </Show>
      </section>
    </div>
  );
}
